import { useEffect, useState } from "react";
import { Link } from "wouter";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { MAX_TRACKING_NUMBERS, paths, type CallLog, type TrackingNumber } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useLeadSources } from "../../hooks/useLeadSources";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PlanLocked } from "../../components/PlanLocked";
import { RingSetup } from "../../components/RingSetup";
import { RingHoursCard } from "../../components/RingHoursCard";

// Setup → Lead sources: the company's own list of where customers come from
// (the website form asks the same list; New customer offers it), and a
// tracking number per source — a real local number that rings the company's
// line and logs every call under its source. Reports → Lead sources adds
// calls, requests, customers and revenue up per source.

const DAY = 86400000;
const fmtPhone = (e164: string) => {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
};
const STATUS_LABEL: Record<CallLog["status"], string> = { ringing: "ringing", answered: "answered", missed: "missed", busy: "busy", failed: "failed" };

export function LeadSourcesPage() {
  const { tenantId } = useAuth();
  const sources = useLeadSources(tenantId);
  const numbersOn = useEntitlements().hasFeature("leadNumbers");
  const [draft, setDraft] = useState<string | null>(null);
  const [numbers, setNumbers] = useState<Array<TrackingNumber & { id: string }>>([]);
  const [calls, setCalls] = useState<Array<CallLog & { id: string }>>([]);
  const [buying, setBuying] = useState<string | null>(null);
  const [areaCode, setAreaCode] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  // Clevis Ring is on once the company agreed to the carrier charges and has a card; numbers can be rented then.
  const [ringReady, setRingReady] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const since = Date.now() - 30 * DAY;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantTrackingNumbers(tenantId)), where("status", "==", "active")), (snap) =>
        setNumbers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TrackingNumber) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantCalls(tenantId)), where("startedAt", ">=", since), orderBy("startedAt", "desc"), limit(200)), (snap) =>
        setCalls(snap.docs.map((d) => ({ id: d.id, ...(d.data() as CallLog) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const list = draft ?? sources.join("\n");
  const numberFor = (s: string) => numbers.find((n) => n.source === s);
  const callsFor = (s: string) => calls.filter((c) => c.source === s);

  async function act(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setNote("");
    try {
      await fn();
      if (done) setNote(done);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!tenantId) return null;
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-lg font-bold">Clevis Ring</h1>
        <p className="text-xs text-steel">
          Where your customers come from, a real phone number for each source (or your main line forwarded in), the caller popped on every office screen, and every call logged. Your website form asks
          this list, New customer offers it, and Reports → Lead sources adds up calls, requests, customers and revenue for each one.
        </p>
      </div>
      {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}

      <Card className="space-y-2">
        <CardTitle>Your sources</CardTitle>
        <textarea
          className="min-h-[120px] w-full rounded-lg border border-steel/30 bg-white px-2 py-1 text-sm text-ink focus:border-brandorange focus:outline-none"
          value={list}
          placeholder={"Google search\nReferral\nManufacturer website"}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <Button
            busy={busy}
            disabled={draft === null}
            onClick={() =>
              void act(async () => {
                await call("setLeadSources", { tenantId, sources: list.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 20) });
                setDraft(null);
              }, "Saved.")
            }
          >
            Save sources
          </Button>
          <span className="text-[11px] text-steel">One per line, up to 20.</span>
        </div>
      </Card>

      {numbersOn ? <RingSetup tenantId={tenantId} onReady={setRingReady} /> : null}
      {numbersOn && ringReady ? <RingHoursCard tenantId={tenantId} /> : null}

      <Card className="space-y-2">
        <CardTitle>Tracking numbers</CardTitle>
        <p className="text-xs text-steel">
          A real local number for one source: put it on that ad, listing or truck. Calls ring your line with the caller's number
          showing, pop on the office's screens with the customer matched by phone, and each one is logged under the source. Keep your
          main number where it is: forward it at your phone company to one of these and its calls get the same treatment. Up to{" "}
          {MAX_TRACKING_NUMBERS} numbers.
        </p>
        {!numbersOn ? <PlanLocked feature="leadNumbers" /> : null}
        <p className="text-[11px] text-steel">Each person picks how calls show on their own screen, a bar, a corner note or nothing, under My profile → Incoming calls. "Pop the caller on screen" below turns it off for everyone on that number.</p>
        <div className={cn("divide-y divide-steel/10", !numbersOn && "pointer-events-none opacity-50")}>
          {sources.map((s) => {
            const n = numberFor(s);
            const recent = callsFor(s);
            return (
              <div key={s} className="py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{s}</span>
                  {n ? (
                    <span className="text-xs text-steel">
                      {recent.length} call{recent.length === 1 ? "" : "s"} in 30 days
                    </span>
                  ) : buying === s ? null : ringReady ? (
                    <Button
                      variant="ghost"
                      className="px-2 py-0.5 text-xs"
                      onClick={() => {
                        setBuying(s);
                        setAreaCode("");
                        setForwardTo("");
                      }}
                    >
                      Get a number
                    </Button>
                  ) : (
                    <span className="text-xs text-steel">Turn on Clevis Ring above to get a number.</span>
                  )}
                </div>
                {n ? (
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink">
                    <span className="font-mono text-sm">{fmtPhone(n.phone)}</span>
                    <label className="flex items-center gap-1">
                      rings
                      <Input
                        className="w-36 py-0.5 text-xs"
                        defaultValue={fmtPhone(n.forwardTo)}
                        onBlur={(e) => {
                          if (e.target.value.trim() && e.target.value.trim() !== fmtPhone(n.forwardTo)) {
                            void act(() => call("updateTrackingNumber", { tenantId, numberId: n.id, forwardTo: e.target.value.trim() }), "Saved.");
                          }
                        }}
                      />
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={n.whisper} onChange={(e) => void act(() => call("updateTrackingNumber", { tenantId, numberId: n.id, whisper: e.target.checked }))} />
                      say the source before connecting
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={n.pop !== false} onChange={(e) => void act(() => call("updateTrackingNumber", { tenantId, numberId: n.id, pop: e.target.checked }))} />
                      pop the caller on screen
                    </label>
                    <label className="flex items-center gap-1" title="How long your line rings before the caller hears you couldn't take the call. Longer than your voicemail's pickup, so it still catches the call.">
                      rings for
                      <Input
                        type="number"
                        min={10}
                        max={60}
                        className="w-14 py-0.5 text-xs"
                        defaultValue={n.ringSeconds ?? 25}
                        onBlur={(e) => {
                          const v = Math.round(Number(e.target.value));
                          if (v >= 10 && v <= 60 && v !== (n.ringSeconds ?? 25)) void act(() => call("updateTrackingNumber", { tenantId, numberId: n.id, ringSeconds: v }), "Saved.");
                        }}
                      />
                      s
                    </label>
                    <label className="flex items-center gap-1" title="Outside working hours (above), calls ring this instead: the on-call phone. Blank = the same line around the clock.">
                      after hours, rings
                      <Input
                        className="w-36 py-0.5 text-xs"
                        placeholder="same line"
                        defaultValue={n.afterHoursForwardTo ? fmtPhone(n.afterHoursForwardTo) : ""}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          const cur = n.afterHoursForwardTo ? fmtPhone(n.afterHoursForwardTo) : "";
                          if (v !== cur) void act(() => call("updateTrackingNumber", { tenantId, numberId: n.id, afterHoursForwardTo: v }), v ? "Saved." : "Cleared: this number rings the same line around the clock.");
                        }}
                      />
                    </label>
                    <ConfirmButton
                      className="px-2 py-0.5 text-xs"
                      busy={busy}
                      confirm={`Give ${fmtPhone(n.phone)} back? Anyone still calling it hears it's out of service.`}
                      confirmLabel="Release"
                      onConfirm={() => void act(() => call("releaseTrackingNumber", { tenantId, numberId: n.id }), "Released.")}
                    >
                      Release
                    </ConfirmButton>
                  </div>
                ) : buying === s ? (
                  <form
                    className="mt-1 flex flex-wrap items-end gap-2 text-xs"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void act(async () => {
                        const r = await call<{ phone: string }>("buyTrackingNumber", { tenantId, source: s, areaCode: areaCode.trim(), forwardTo: forwardTo.trim() });
                        setBuying(null);
                        setNote(`${fmtPhone(r.phone)} is your ${s} number. Put it on that ad.`);
                      });
                    }}
                  >
                    <div>
                      <Label>Area code</Label>
                      <Input className="w-20 py-1 text-xs" required pattern="\d{3}" inputMode="numeric" value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="702" />
                    </div>
                    <div>
                      <Label>Calls ring</Label>
                      <Input className="w-40 py-1 text-xs" required value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} placeholder="your main line" />
                    </div>
                    <Button type="submit" busy={busy} className="px-3 py-1 text-xs">
                      Get number
                    </Button>
                    <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setBuying(null)}>
                      Cancel
                    </Button>
                  </form>
                ) : null}
              </div>
            );
          })}
          {sources.length === 0 ? <p className="py-2 text-xs text-steel">Add your sources above first.</p> : null}
        </div>
      </Card>

      <Card className="p-0">
        <CardTitle className="px-4 pt-3">Calls in the last 30 days ({calls.length})</CardTitle>
        {calls.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-xs last:border-0">
            <span className="min-w-0 text-ink">
              <span className="font-semibold">{c.source}</span>
              {" · "}
              {c.customerId ? (
                <Link href={`/customers/${c.customerId}`} className="text-brandorange-dark hover:underline">
                  {c.customerName || fmtPhone(c.from)}
                </Link>
              ) : (
                <span>{fmtPhone(c.from) || "unknown caller"}</span>
              )}
              {!c.customerId ? <span className="text-steel"> · new</span> : null}
              {c.afterHours ? <span className="text-steel"> · after hours → {fmtPhone(c.forwardTo)}</span> : null}
            </span>
            <span className={cn("shrink-0", c.status === "missed" || c.status === "busy" || c.status === "failed" ? "text-danger" : "text-steel")}>
              {new Date(c.startedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {STATUS_LABEL[c.status]}
              {c.durationSec ? ` · ${Math.round(c.durationSec / 60)} min` : ""}
            </span>
          </div>
        ))}
        {calls.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">No calls yet.</p> : null}
      </Card>
    </div>
  );
}
